import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, ComposedChart, Bar,
  Label,
} from "recharts";
import {
  RefreshCw, TrendingUp, Target, Crown, Eye,
  Calendar, Zap, ChevronDown, ChevronUp, BarChart3
} from "lucide-react";

const API = getApiUrl() + "/api";
import getApiUrl from '../utils/getApiUrl';
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { ...(opts.headers || {}), "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

export default function PerformanceTimelineChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState("conversions");
  const [showEvents, setShowEvents] = useState(true);
  const [period, setPeriod] = useState("60");
  const [expandedAnnotation, setExpandedAnnotation] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await authFetch(API + "/google-ads/performance-timeline");
      if (res.ok) {
        const d = await res.json();
        setData(d);
      } else {
        toast.error("Erreur chargement timeline");
      }
    } catch (e) { console.error(e); toast.error("Erreur: " + e.message); }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <RefreshCw className="w-7 h-7 text-amber-400 animate-spin" />
      <p className="text-sm text-slate-400">Chargement des donnees (60 jours)...</p>
    </div>
  );
  if (!data?.timeline?.length) return null;

  const timeline = period === "30" ? data.timeline.slice(-30) : data.timeline;
  const events = data.events || [];
  const annotations = data.annotations || [];
  const summary = data.summary || {};

  // Format chart data
  const chartData = timeline.map(d => ({
    ...d,
    date_short: d.date?.slice(5),
    date_label: d.date?.slice(8) + "/" + d.date?.slice(5, 7),
  }));

  const METRICS = [
    { key: "conversions", label: "Leads / jour", color: "#4ade80", yKey: "conversions", rolling: "conv_7d_avg", icon: <TrendingUp className="w-4 h-4" /> },
    { key: "cpa", label: "CPA (€)", color: "#f59e0b", yKey: "cpa", rolling: "cpa_7d_avg", icon: <Target className="w-4 h-4" />, invert: true },
    { key: "abs_top_pct", label: "Position #1 (%)", color: "#818cf8", yKey: "abs_top_pct", icon: <Crown className="w-4 h-4" /> },
    { key: "clicks", label: "Clics / jour", color: "#38bdf8", yKey: "clicks", icon: <BarChart3 className="w-4 h-4" /> },
    { key: "cost", label: "Depense (€)", color: "#fb7185", yKey: "cost", icon: <Eye className="w-4 h-4" /> },
  ];
  const activeMeta = METRICS.find(m => m.key === activeMetric) || METRICS[0];

  // Map annotations to chart date_labels for reference lines
  const annotationMap = {};
  for (const ann of annotations) {
    const entry = chartData.find(cd => cd.date === ann.date);
    if (entry) {
      annotationMap[entry.date_label] = ann;
    }
  }

  // Find event dates for reference lines
  const eventDateLabels = Object.keys(annotationMap);

  // Compute before/after for events
  const eventDates = [...new Set(events.map(e => e.date))];
  const eventImpact = eventDates.map(date => {
    const idx = chartData.findIndex(d => d.date === date);
    if (idx < 0) return null;
    const before7 = chartData.slice(Math.max(0, idx - 7), idx).filter(d => d.conversions != null);
    const after7 = chartData.slice(idx, Math.min(chartData.length, idx + 7)).filter(d => d.conversions != null);
    const avgBefore = before7.length ? before7.reduce((s, d) => s + (d.conversions || 0), 0) / before7.length : 0;
    const avgAfter = after7.length ? after7.reduce((s, d) => s + (d.conversions || 0), 0) / after7.length : 0;
    const cpaBefore = before7.length ? before7.filter(d => d.cpa).reduce((s, d) => s + d.cpa, 0) / before7.filter(d => d.cpa).length : 0;
    const cpaAfter = after7.length ? after7.filter(d => d.cpa).reduce((s, d) => s + d.cpa, 0) / after7.filter(d => d.cpa).length : 0;
    const dayEvents = events.filter(e => e.date === date);
    return {
      date,
      events: dayEvents,
      leads_before: +avgBefore.toFixed(1),
      leads_after: +avgAfter.toFixed(1),
      leads_delta: +(avgAfter - avgBefore).toFixed(1),
      cpa_before: +cpaBefore.toFixed(1),
      cpa_after: +cpaAfter.toFixed(1),
      cpa_delta: +(cpaAfter - cpaBefore).toFixed(1),
    };
  }).filter(Boolean);

  return (
    <div className="space-y-5" data-testid="performance-timeline">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-base flex items-center gap-2 text-slate-200">
          <Calendar className="w-5 h-5 text-amber-400" />
          Evolution & Impact des modifications
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-900/60 rounded-lg overflow-hidden text-xs">
            {[["30", "30j"], ["60", "60j"]].map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 ${period === k ? "bg-slate-700/60 text-slate-200" : "text-slate-400 hover:text-slate-300"}`}
                data-testid={`period-${k}`}>{l}</button>
            ))}
          </div>
          <button onClick={loadData} disabled={loading} className="p-2 text-slate-400 hover:text-slate-300" data-testid="refresh-timeline">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Leads/jour (moy)" value={`${summary.avg_daily_conversions}`} sub={`sur ${summary.days} jours`} />
        <SummaryCard label="CPA moyen" value={`${summary.avg_cpa}€`} sub="cout par lead" />
        <SummaryCard label="Modifications" value={`${summary.total_modifications}`} sub="appliquees" accent />
        <SummaryCard label="Jours analyses" value={`${summary.days}`} sub="de donnees" />
      </div>

      {/* Metric Selector */}
      <div className="flex gap-1 bg-slate-900/60 rounded-xl p-1 border border-slate-700/40 overflow-x-auto">
        {METRICS.map(m => (
          <button key={m.key} onClick={() => setActiveMetric(m.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeMetric === m.key
                ? "text-white border border-slate-600/50" : "text-slate-400 hover:text-slate-300"
            }`}
            style={activeMetric === m.key ? { backgroundColor: m.color + "20", borderColor: m.color + "40" } : {}}
            data-testid={`metric-${m.key}`}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      {/* Chart with Annotations */}
      <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4" data-testid="main-chart">
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={chartData} margin={{ top: 15, right: 55, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.5} />
            <XAxis dataKey="date_label" tick={{ fill: "#64748b", fontSize: 11 }} interval={Math.floor(chartData.length / 12)} />
            <YAxis tick={{ fill: "#64748b", fontSize: 11 }} width={50} />
            <Tooltip content={<CustomTooltip events={events} showEvents={showEvents} />} />

            {/* Event reference lines with annotation labels */}
            {showEvents && eventDateLabels.map(dateLabel => {
              const ann = annotationMap[dateLabel];
              if (!ann) return null;
              const isBudget = ann.types.includes("increase_budget");
              return (
                <ReferenceLine
                  key={dateLabel}
                  x={dateLabel}
                  stroke={isBudget ? "#f59e0b" : "#818cf8"}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  strokeOpacity={0.8}
                  label={<AnnotationLabel annotation={ann} />}
                />
              );
            })}

            {/* Area fill */}
            <Area type="monotone" dataKey={activeMeta.yKey} fill={activeMeta.color} fillOpacity={0.08} stroke="none" />
            {/* Main line */}
            <Line type="monotone" dataKey={activeMeta.yKey} stroke={activeMeta.color} strokeWidth={2}
              dot={false} activeDot={{ r: 4, fill: activeMeta.color }} connectNulls />
            {/* Rolling average if available */}
            {activeMeta.rolling && (
              <Line type="monotone" dataKey={activeMeta.rolling} stroke={activeMeta.color}
                strokeWidth={2.5} strokeDasharray="6 3" dot={false} connectNulls strokeOpacity={0.6} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-between mt-2 px-2">
          <div className="flex items-center gap-4 text-[10px] text-slate-500">
            <span className="flex items-center gap-1">
              <span className="w-4 h-0.5 rounded" style={{ backgroundColor: activeMeta.color }} /> {activeMeta.label}
            </span>
            {activeMeta.rolling && (
              <span className="flex items-center gap-1">
                <span className="w-4 h-0.5 rounded border-b border-dashed" style={{ borderColor: activeMeta.color }} /> Moyenne 7j
              </span>
            )}
            {showEvents && (
              <>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-0.5 rounded bg-amber-500" style={{ opacity: 0.7 }} /> Budget
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-0.5 rounded bg-indigo-400" style={{ opacity: 0.7 }} /> Enchere
                </span>
              </>
            )}
          </div>
          <button onClick={() => setShowEvents(!showEvents)} className="text-[10px] text-slate-500 hover:text-slate-400 flex items-center gap-1"
            data-testid="toggle-events">
            <Zap className="w-3 h-3" /> {showEvents ? "Masquer" : "Afficher"} modifs
          </button>
        </div>
      </div>

      {/* Annotation Detail Cards (below chart) */}
      {showEvents && annotations.length > 0 && (
        <div className="space-y-2" data-testid="annotation-cards">
          <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            Modifications appliquees ({annotations.length} date{annotations.length > 1 ? "s" : ""})
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {annotations.map((ann, i) => {
              const isExpanded = expandedAnnotation === ann.date;
              const isBudget = ann.types.includes("increase_budget");
              return (
                <button
                  key={ann.date}
                  onClick={() => setExpandedAnnotation(isExpanded ? null : ann.date)}
                  className={`flex-shrink-0 text-left rounded-lg border p-3 transition-all min-w-[180px] max-w-[280px] ${
                    isBudget
                      ? "bg-amber-500/8 border-amber-500/25 hover:border-amber-500/40"
                      : "bg-indigo-500/8 border-indigo-500/25 hover:border-indigo-500/40"
                  }`}
                  data-testid={`annotation-card-${ann.date}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${isBudget ? "text-amber-300" : "text-indigo-300"}`}>
                      {ann.date.slice(8)}/{ann.date.slice(5, 7)}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      isBudget ? "bg-amber-500/20 text-amber-300" : "bg-indigo-500/20 text-indigo-300"
                    }`}>
                      {ann.count} modif{ann.count > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {(isExpanded ? ann.descriptions : ann.descriptions.slice(0, 2)).map((desc, j) => (
                      <p key={j} className="text-[11px] text-slate-300 leading-tight">{desc}</p>
                    ))}
                    {!isExpanded && ann.descriptions.length > 2 && (
                      <p className="text-[10px] text-slate-500">+{ann.descriptions.length - 2} autres...</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Dual Chart: Leads + CPA side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniChart data={chartData} dataKey="conversions" rollingKey="conv_7d_avg" color="#4ade80" label="Leads / jour" annotationMap={annotationMap} showEvents={showEvents} />
        <MiniChart data={chartData} dataKey="cpa" rollingKey="cpa_7d_avg" color="#f59e0b" label="CPA (€)" annotationMap={annotationMap} showEvents={showEvents} />
      </div>

      {/* Impact Analysis */}
      {eventImpact.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl overflow-hidden" data-testid="impact-analysis">
          <div className="px-6 py-4 border-b border-slate-700/30">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Analyse d'impact des modifications
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {eventImpact.map((ei, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-700/30 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-amber-300 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {ei.date}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {ei.events.map((ev, j) => (
                        <p key={j} className="text-xs text-slate-400">
                          {ev.description || (
                            <>
                              {ev.type === "increase_budget" ? "Budget" : ev.type === "increase_bid" ? "Enchere" : ev.type}
                              {" "}<span className="text-slate-300">{ev.keyword}</span>
                              {ev.old_value > 0 && <> : {ev.old_value}€ → <span className="text-amber-300">{ev.new_value}€</span></>}
                            </>
                          )}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ImpactMetric label="Leads/jour" before={ei.leads_before} after={ei.leads_after} delta={ei.leads_delta} unit="" />
                  <ImpactMetric label="CPA" before={ei.cpa_before} after={ei.cpa_after} delta={ei.cpa_delta} unit="€" invert />
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Comparaison moyenne 7j avant vs 7j apres la modification</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Annotation label rendered on chart ---- */
function AnnotationLabel({ viewBox, annotation }) {
  if (!viewBox || !annotation) return null;
  const { x } = viewBox;
  const isBudget = annotation.types?.includes("increase_budget");
  const hasBid = annotation.types?.includes("increase_bid");
  const bgColor = isBudget ? "#78350f" : "#312e81";
  const borderColor = isBudget ? "#f59e0b" : "#818cf8";
  const textColor = isBudget ? "#fcd34d" : "#c7d2fe";

  // Build label showing the count and the most important action
  let displayLabel;
  if (isBudget && hasBid) {
    displayLabel = `Budget + ${annotation.count - 1} CPC`;
  } else if (isBudget) {
    displayLabel = `Budget modifie`;
  } else {
    displayLabel = `${annotation.count} CPC modif${annotation.count > 1 ? "s" : ""}`;
  }
  const pillWidth = Math.max(90, displayLabel.length * 6.5 + 16);

  return (
    <g>
      {/* Small diamond marker at top of reference line */}
      <polygon
        points={`${x},8 ${x+6},14 ${x},20 ${x-6},14`}
        fill={borderColor}
        fillOpacity={0.9}
      />
      {/* Background pill inside chart area */}
      <rect
        x={x - pillWidth / 2}
        y={22}
        width={pillWidth}
        height={20}
        rx={4}
        fill={bgColor}
        stroke={borderColor}
        strokeWidth={0.7}
        fillOpacity={0.92}
      />
      {/* Text */}
      <text
        x={x}
        y={35}
        textAnchor="middle"
        fill={textColor}
        fontSize={9}
        fontWeight={600}
        fontFamily="system-ui, sans-serif"
      >
        {displayLabel}
      </text>
    </g>
  );
}

/* ---- Sub-components ---- */

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "bg-amber-500/10 border-amber-500/25" : "bg-slate-800/50 border-slate-700/40"}`}>
      <p className={`text-xl font-bold ${accent ? "text-amber-300" : "text-slate-200"}`}>{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-[10px] text-slate-500">{sub}</p>
    </div>
  );
}

function MiniChart({ data, dataKey, rollingKey, color, label, annotationMap, showEvents }) {
  const eventDateLabels = Object.keys(annotationMap || {});
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4">
      <p className="text-xs font-medium text-slate-400 mb-2">{label}</p>
      <ResponsiveContainer width="100%" height={160}>
        <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.3} />
          <XAxis dataKey="date_label" tick={{ fill: "#475569", fontSize: 9 }} interval={Math.floor(data.length / 6)} />
          <YAxis tick={{ fill: "#475569", fontSize: 9 }} width={40} />
          <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: "11px" }}
            labelStyle={{ color: "#94a3b8" }} itemStyle={{ color: color }} />
          {showEvents && eventDateLabels.map(dateLabel => {
            const ann = annotationMap[dateLabel];
            const isBudget = ann?.types?.includes("increase_budget");
            return (
              <ReferenceLine
                key={dateLabel}
                x={dateLabel}
                stroke={isBudget ? "#f59e0b" : "#818cf8"}
                strokeDasharray="3 3"
                strokeWidth={1}
                strokeOpacity={0.6}
              />
            );
          })}
          <Area type="monotone" dataKey={dataKey} fill={color} fillOpacity={0.06} stroke="none" />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} dot={false} connectNulls />
          {rollingKey && (
            <Line type="monotone" dataKey={rollingKey} stroke={color} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls strokeOpacity={0.5} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ImpactMetric({ label, before, after, delta, unit, invert }) {
  const isGood = invert ? delta < 0 : delta > 0;
  const isBad = invert ? delta > 0 : delta < 0;
  return (
    <div className="bg-slate-800/60 rounded-lg p-3">
      <p className="text-[10px] text-slate-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">{before}{unit}</span>
        <span className="text-slate-600">→</span>
        <span className="text-sm font-bold text-slate-200">{after}{unit}</span>
        {delta !== 0 && (
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isGood ? "bg-green-500/20 text-green-400" : isBad ? "bg-red-500/20 text-red-400" : "text-slate-500"}`}>
            {delta > 0 ? "+" : ""}{delta}{unit}
          </span>
        )}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label, events, showEvents }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const dayEvents = showEvents ? events.filter(e => e.date === d?.date) : [];
  const isPlaceholder = d?.conversions == null;

  return (
    <div className="bg-slate-900/95 border border-slate-700/60 rounded-xl p-3 text-xs max-w-[280px] shadow-xl backdrop-blur">
      <p className="text-slate-300 font-medium mb-1.5">{d?.date}</p>
      {isPlaceholder ? (
        <p className="text-slate-500 italic text-[10px]">Donnees pas encore disponibles (jour en cours)</p>
      ) : (
        <>
          <div className="space-y-1">
            {payload.filter(p => p.value != null).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <span className="text-slate-400 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                  {p.name === "conv_7d_avg" ? "Moy. 7j" : p.name === "cpa_7d_avg" ? "CPA Moy. 7j" : p.name}
                </span>
                <span className="font-bold text-slate-200">{typeof p.value === "number" ? p.value.toFixed(1) : p.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-1.5 pt-1.5 border-t border-slate-700/40 space-y-0.5 text-[10px] text-slate-500">
            <p>Clics: {d.clicks} | Impr: {d.impressions}</p>
            <p>Cout: {d.cost}€ | CPC: {d.avg_cpc}€</p>
          </div>
        </>
      )}
      {dayEvents.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-amber-500/30">
          <p className="text-amber-400 font-medium mb-0.5 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Modifications du jour :
          </p>
          {dayEvents.map((ev, i) => (
            <p key={i} className="text-slate-300 text-[10px] leading-relaxed">
              {ev.description || `${ev.type} — ${ev.keyword?.slice(0, 30)}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
